import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/* ---------- Statuts métier (une seule source de vérité) ---------- */
const STATUS = {
  // devis
  draft: ["Brouillon", "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]"],
  sent: ["Envoyé", "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]"],
  viewed: ["Vu par le client", "bg-teal-50 text-teal-700 border-teal-200"],
  accepted: ["Signé", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  refused: ["Refusé", "bg-rose-50 text-rose-700 border-rose-200"],
  // factures
  brouillon: ["Brouillon", "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]"],
  envoyee: ["Envoyée", "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]"],
  vue: ["Vue", "bg-teal-50 text-teal-700 border-teal-200"],
  partielle: ["Partiel", "bg-amber-50 text-amber-700 border-amber-200"],
  payee: ["Payée", "bg-emerald-50 text-emerald-800 border-emerald-300"],
  en_retard: ["En retard", "bg-rose-50 text-rose-800 border-rose-300"],
  // dossiers
  nouveau: ["Nouveau", "bg-slate-100 text-[#0F2645] border-[#E2E7F7]"],
  visite_a_effectuer: ["Visite à effectuer", "bg-amber-50 text-amber-700 border-amber-200"],
  en_preparation: ["En préparation", "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]"],
  devis_a_creer: ["Devis à créer", "bg-orange-50 text-orange-700 border-orange-200"],
  devis_envoye: ["Devis envoyé", "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]"],
  en_attente: ["En attente", "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]"],
  accepte: ["Accepté", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  refuse: ["Refusé", "bg-rose-50 text-rose-700 border-rose-200"],
  a_relancer: ["À relancer", "bg-amber-50 text-amber-700 border-amber-200"],
  chantier_en_cours: ["Chantier en cours", "bg-[#EEF1FC] text-[#2F4CA8] border-[#C9D4F4]"],
  chantier_termine: ["Terminé", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  archive: ["Archivé", "bg-slate-100 text-[#93A0BC] border-[#E2E7F7]"],
  // divers
  signe_valide: ["Signée", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  particulier: ["Particulier", "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]"],
  professionnel: ["Professionnel", "bg-indigo-50 text-indigo-700 border-indigo-200"],
};

export const statusMeta = (value) => {
  const found = STATUS[value];
  return { label: found ? found[0] : value, cls: found ? found[1] : "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]" };
};

export const StatusBadge = ({ value, testId }) => {
  if (!value) return null;
  const { label, cls } = statusMeta(value);
  return (
    <span
      data-testid={testId}
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
};

/* ---------- Surfaces ---------- */
export const Card = ({ className = "", children, ...rest }) => (
  <div
    className={`rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)] ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const SectionCard = ({ title, hint, action, className = "", children, testId }) => (
  <Card className={`overflow-hidden ${className}`} data-testid={testId}>
    <div className="flex items-start justify-between gap-3 border-b border-[#E2E7F7] px-4 py-3 sm:px-5">
      <div className="min-w-0">
        <h2 className="truncate font-heading text-[15px] font-bold tracking-tight text-[#0F2645]">{title}</h2>
        {hint && <p className="mt-0.5 truncate text-xs text-[#5C6A87]">{hint}</p>}
      </div>
      {action}
    </div>
    <div className="px-4 py-3.5 sm:px-5">{children}</div>
  </Card>
);

/* ---------- Métriques ---------- */
export const MetricCard = ({ label, value, hint, icon: Icon, tone = "blue", to, testId, delay = 0 }) => {
  const tones = {
    blue: ["text-[#3A5CC8] bg-[#EEF1FC]", "#3A5CC8"],
    emerald: ["text-emerald-700 bg-emerald-50", "#059669"],
    amber: ["text-amber-700 bg-amber-50", "#D97706"],
    rose: ["text-rose-700 bg-rose-50", "#E11D48"],
    slate: ["text-[#5C6A87] bg-[#F3F5FC]", "#CBD5E1"],
  };
  const [badge, accent] = tones[tone] || tones.blue;
  const body = (
    <>
      <span className="absolute bottom-4 left-0 top-4 w-[3px] rounded-r-full" style={{ backgroundColor: accent }} />
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#5C6A87]">{label}</p>
        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${badge}`}>
          {Icon && <Icon className="h-4 w-4" strokeWidth={2.2} />}
        </span>
      </div>
      <p className="mt-2 truncate font-mono text-[22px] font-bold leading-none tracking-tight tabular-nums text-[#0F2645] sm:text-[26px]">
        {value}
      </p>
      <div className="mt-2 flex items-center justify-between gap-2">
        {hint ? <p className="truncate text-xs text-[#93A0BC]">{hint}</p> : <span />}
        {to && <ChevronRight className="h-4 w-4 shrink-0 text-[#C9D4F4] transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-[#3A5CC8]" />}
      </div>
    </>
  );
  const cls =
    "dv-card-in group relative block min-w-0 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white py-4 pl-5 pr-4 text-left shadow-[0_1px_2px_rgba(15,38,69,0.04)] transition-[border-color,background-color] duration-150 hover:border-[#C9D4F4] hover:bg-[#FBFCFE] active:scale-[0.995]";
  const style = { animationDelay: `${delay}ms` };
  return to ? (
    <Link to={to} data-testid={testId} className={cls} style={style}>{body}</Link>
  ) : (
    <div data-testid={testId} className={cls} style={style}>{body}</div>
  );
};

/* ---------- Ligne de liste dense (terrain) ---------- */
export const ListRow = ({ to, onClick, bar = "bg-slate-200", title, subtitle, right, status, testId }) => {
  const inner = (
    <>
      <span className={`h-9 w-1 shrink-0 rounded-full ${bar}`} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-[#0F2645]">{title}</span>
          {status && <StatusBadge value={status} />}
        </span>
        {subtitle && <span className="mt-0.5 block truncate text-xs text-[#5C6A87]">{subtitle}</span>}
      </span>
      {right && <span className="shrink-0 text-right text-sm font-bold tabular-nums text-[#0F2645]">{right}</span>}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
    </>
  );
  const cls =
    "flex min-h-[56px] w-full items-center gap-3 rounded-xl border border-[#E2E7F7] bg-white px-3 py-2.5 text-left transition-colors duration-150 hover:border-[#C9D4F4] hover:bg-[#F6F8FD] active:scale-[0.995]";
  return to ? (
    <Link to={to} data-testid={testId} className={cls}>{inner}</Link>
  ) : (
    <button type="button" onClick={onClick} data-testid={testId} className={cls}>{inner}</button>
  );
};

/* ---------- États ---------- */
export const EmptyState = ({ icon: Icon, title, hint, action, testId }) => (
  <div data-testid={testId} className="flex flex-col items-start gap-2 rounded-2xl border border-dashed border-[#C9D4F4] bg-[#F6F8FD]/70 px-5 py-7">
    {Icon && (
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EEF1FC] text-[#3A5CC8] ring-1 ring-[#C9D4F4]">
        <Icon className="h-5 w-5" />
      </span>
    )}
    <p className="mt-1 font-heading text-sm font-bold text-[#0F2645]">{title}</p>
    {hint && <p className="max-w-md text-xs leading-relaxed text-[#5C6A87]">{hint}</p>}
    {action}
  </div>
);

export const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-slate-200/70 ${className}`} />
);

/* Squelette de liste (3 lignes) */
export const ListSkeleton = ({ rows = 3, testId = "list-skeleton" }) => (
  <div data-testid={testId} className="space-y-2 p-4" aria-busy="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 rounded-xl border border-[#E2E7F7] bg-white p-3">
        <Skeleton className="h-9 w-1.5 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/3" /></div><Skeleton className="h-5 w-16 rounded-full" />
      </div>
    ))}
  </div>
);

/* Squelette de page document (devis, facture) pendant le chargement */
export const PageSkeleton = ({ testId = "page-skeleton" }) => (
  <div data-testid={testId} className="mt-6 space-y-4" aria-busy="true" aria-live="polite">
    <div className="flex items-center gap-3"><Skeleton className="h-6 w-28" /><Skeleton className="h-6 w-20 rounded-full" /><Skeleton className="ml-auto h-7 w-32" /></div>
    <div className="rounded-2xl border border-[#E2E7F7] bg-white p-5">
      <Skeleton className="h-5 w-48" />
      <div className="mt-4 grid gap-3 sm:grid-cols-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-3/5" /></div>
      <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="flex items-center gap-3"><Skeleton className="h-6 w-6" /><Skeleton className="h-4 flex-1" /><Skeleton className="h-4 w-16" /></div>)}</div>
    </div>
    <div className="grid gap-4 lg:grid-cols-2"><Skeleton className="h-36" /><Skeleton className="h-36" /></div>
  </div>
);

/* ---------- Barre d'action mobile (zone du pouce) ---------- */
export const MobileActionBar = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="fixed bottom-[68px] left-0 right-0 z-30 flex items-center gap-2 border-t border-[#E2E7F7] bg-white/95 px-4 py-3 shadow-[0_-4px_20px_rgba(11,30,61,0.10)] backdrop-blur-md lg:hidden"
  >
    {children}
  </div>
);
